import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './PdfSideNavigation.module.scss';

/**
 * PdfSideNavigation Component
 *
 * Renders a collapsible side navigation menu for PDF climate disclosure reports.
 * Each section can be expanded/collapsed individually, or all sections can be toggled at once.
 * Clicking on an item triggers the `onItemClick` callback with the flat index.
 *
 * @component
 * @param {Object} props
 * @param {Array<{title: string, items: string[]}>} props.data - Array of navigation sections, each with a title and list of items.
 * @param {function} [props.onItemClick] - Callback fired when a navigation item is clicked. Receives the flat index number.
 * @param {number} [props.activeItem] - The flat index of the currently active navigation item.
 * @param {string} [props.className] - Additional className(s) for the navigation container.
 */

function PdfSideNavigation({ data = [], onItemClick = null, activeItem = null, className = '' }) {
  const [expandedSections, setExpandedSections] = useState(new Set([0, 1])); // Start with first two sections expanded
  const [isAllExpanded, setIsAllExpanded] = useState(true);

  const toggleSection = useCallback(
    (index) => {
      setExpandedSections((prevExpanded) => {
        const newExpanded = new Set(prevExpanded);
        if (newExpanded.has(index)) {
          newExpanded.delete(index);
        } else {
          newExpanded.add(index);
        }

        // Update isAllExpanded based on new state
        setIsAllExpanded(newExpanded.size === data?.length);

        return newExpanded;
      });
    },
    [data.length],
  );

  const toggleAll = useCallback(() => {
    if (isAllExpanded) {
      setExpandedSections(new Set());
      setIsAllExpanded(false);
    } else {
      setExpandedSections(new Set(data.map((_, index) => index)));
      setIsAllExpanded(true);
    }
  }, [isAllExpanded, data]);

  // Fixed getFlatIndex function
  const getFlatIndex = useCallback(
    (sectionIndex, itemIndex) => {
      let flatIndex = 0;
      for (let i = 0; i < sectionIndex; i++) {
        flatIndex += data[i]?.items?.length || 0; // Fixed: use 'items' instead of 'item'
      }
      return flatIndex + itemIndex; // Fixed: return the calculated value
    },
    [data],
  );

  // Fixed handleItemClick function
  const handleItemClick = useCallback(
    (sectionIndex, itemIndex) => {
      const flatIndex = getFlatIndex(sectionIndex, itemIndex);
      if (onItemClick) {
        onItemClick(flatIndex); // Pass only the flat index
      }
    },
    [onItemClick, getFlatIndex],
  );

  // Helper function to check if an item is active based on flat index
  const isItemActive = useCallback(
    (sectionIndex, itemIndex) => {
      if (activeItem === null || activeItem === undefined) return false;
      const itemFlatIndex = getFlatIndex(sectionIndex, itemIndex);
      return itemFlatIndex === activeItem;
    },
    [activeItem, getFlatIndex],
  );

  const ChevronIcon = ({ className = '' }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <div className={`${styles.navigationContainer} ${className}`}>
      <button
        onClick={toggleAll}
        className={`${styles.openAllButton} ${isAllExpanded ? styles.allExpanded : ''}`}
        aria-label={isAllExpanded ? 'Collapse all sections' : 'Expand all sections'}>
        <span>{isAllExpanded ? 'Close all' : 'Open all'}</span>
        <ChevronIcon />
      </button>

      <ul className={styles.pdfSideNavWrap}>
        {data.map((section, sectionIndex) => (
          <li key={`${section.title}-${sectionIndex}`} className={styles.navigationSection}>
            <button
              className={`${styles.menuHeader} ${expandedSections.has(sectionIndex) ? styles.expanded : ''}`}
              onClick={() => toggleSection(sectionIndex)}
              aria-expanded={expandedSections.has(sectionIndex)}
              aria-controls={`submenu-${sectionIndex}`}>
              <span>{section.title}</span>
              <ChevronIcon />
            </button>

            <ul
              id={`submenu-${sectionIndex}`}
              className={`${styles.subMenu} ${expandedSections.has(sectionIndex) ? styles.expanded : ''}`}
              role="region"
              aria-labelledby={`header-${sectionIndex}`}>
              {section.items.map((item, itemIndex) => {
                const isActive = isItemActive(sectionIndex, itemIndex);

                return (
                  <li
                    key={`${item}-${itemIndex}`}
                    className={`${styles.subMenuItem} ${isActive ? styles.active : ''}`}
                    onClick={() => handleItemClick(sectionIndex, itemIndex)} // Fixed: pass correct parameters
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleItemClick(sectionIndex, itemIndex); // Fixed: pass correct parameters
                      }
                    }}>
                    {item}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

PdfSideNavigation.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  onItemClick: PropTypes.func,
  activeItem: PropTypes.number, // Fixed: should be number, not object
  className: PropTypes.string,
};

export default PdfSideNavigation;
